// Takt-Attribute (Akkordsymbol, Tonart, Taktart) – pure MusicXML-DOM-Logik, aufgegangen aus den
// früheren Schein-Composables `useChordEditing`/`useKeySignatureEditing`/`useTimeSignatureEditing`
// (sie hielten kein ref/computed/watch, nur Funktions-Bündel, die als Deps-Objekte durch die
// OSMD-Composables gefädelt wurden – jetzt normale Modul-Importe). Abhängigkeitsregel im Ordner:
// geteilte Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN
// nicht. Dieses Modul hängt nur an `core`.
import {
    findMeasureInPart,
    findMeasureInPartByIndex,
    findPart,
    getPartDivisions,
    getParts,
    getVoiceNotes,
    noteDurationTicks,
    parseMusicXml,
    serializeMusicXml,
    voiceOf,
} from './core';

// ============ Akkordsymbole (<harmony>) ============

export interface ChordData {
    root: string;
    alter: string;
    kind: string;
    bass?: { step: string; alter: string };
}

const KIND_TO_SYMBOL: Record<string, string> = {
    'major': '',
    'minor': 'm',
    'dominant': '7',
    'major-seventh': 'maj7',
    'minor-seventh': 'm7',
    'diminished': 'dim',
    'augmented': 'aug',
    'suspended-second': 'sus2',
    'suspended-fourth': 'sus4',
    'major-sixth': '6',
    'minor-sixth': 'm6',
    'dominant-ninth': '9',
};

const SYMBOL_TO_KIND: Record<string, string> = {
    '': 'major',
    'm': 'minor',
    '7': 'dominant',
    'maj7': 'major-seventh',
    'm7': 'minor-seventh',
    'dim': 'diminished',
    'aug': 'augmented',
    'sus2': 'suspended-second',
    'sus4': 'suspended-fourth',
    '6': 'major-sixth',
    'm6': 'minor-sixth',
    '9': 'dominant-ninth',
};

export function mapKindToSymbol(kind: string): string {
    return KIND_TO_SYMBOL[kind] || '';
}

export function mapSymbolToKind(symbol: string): string {
    return SYMBOL_TO_KIND[symbol] || 'major';
}

export function parseChordSymbol(chord: string): ChordData {
    const match = chord.match(/^([A-G])([#b]?)(.*)$/);
    if (!match) return {root: 'C', alter: '', kind: 'major'};

    const root = match[1] ?? '';
    const alter = match[2] === '#' ? '1' : match[2] === 'b' ? '-1' : '';
    let remaining = match?.[3] ?? '';

    let bass: { step: string; alter: string } | undefined;
    const bassMatch = remaining.match(/\/([A-G])([#b]?)$/);
    if (bassMatch) {
        bass = {
            step: bassMatch[1] ?? '',
            alter: bassMatch[2] === '#' ? '1' : bassMatch[2] === 'b' ? '-1' : ''
        };
        remaining = remaining.replace(/\/[A-G][#b]?$/, '');
    }

    const kind = mapSymbolToKind(remaining);

    return {root, alter, kind, bass};
}

/**
 * Akkordtext aus den Feldern des Akkord-Editors, z. B. `C` + `m` + Bass `Eb` → `Cm/Eb`.
 *
 * Ohne Grundton `''`: der Editor speichert bei jeder Änderung und springt weiter, und
 * `parseChordSymbol` machte aus `/E` oder `m` stillschweigend C-Dur. So lassen sich Basston oder
 * Qualität zuerst wählen, gespeichert wird erst mit dem Grundton. Ein Basston gleich dem Grundton
 * entfällt. Welchen Knopf der Basston auf der Bass-Seite greift, entscheidet die Engine
 * (`bass_layout.bass_slots_for_chord`).
 */
export function composeChordSymbol(fields: {
    root: string;
    accidental: string;
    quality: string;
    bass: string;
}): string {
    if (!fields.root) return '';
    const root = fields.root + fields.accidental;
    const bass = fields.bass && fields.bass !== root ? '/' + fields.bass : '';
    return root + fields.quality + bass;
}

export function getChordFromMeasure(
    musicXml: null | string,
    measureNumber: number,
    partId?: string,
    measureIndex?: number,
): string | null {
    try {
        if (!musicXml) return null;
        const doc = parseMusicXml(musicXml);

        // Part-Scoping: Akkordsymbole leben am Melodie-Part (Default = erster Part). Ein
        // globales querySelectorAll('measure') träfe in mehrteiligen Partituren je nach
        // Taktnummern-Lage einen beliebigen Part.
        const part = findPart(doc, partId);
        const measure = part
            ? (measureIndex === undefined
                ? findMeasureInPart(part, measureNumber)
                : findMeasureInPartByIndex(part, measureIndex))
            : null;
        const harmony = measure?.querySelector('harmony');
        if (!harmony) return null;

        const root = harmony.querySelector('root');
        const rootStep = root?.querySelector('root-step')?.textContent || '';
        const rootAlter = root?.querySelector('root-alter')?.textContent;
        const kind = harmony.querySelector('kind')?.textContent || '';

        let chord = rootStep;
        if (rootAlter === '1') chord += '#';
        else if (rootAlter === '-1') chord += 'b';

        chord += mapKindToSymbol(kind);

        const bass = harmony.querySelector('bass');
        if (bass) {
            const bassStep = bass.querySelector('bass-step')?.textContent || '';
            const bassAlter = bass.querySelector('bass-alter')?.textContent;
            let bassNote = bassStep;
            if (bassAlter === '1') bassNote += '#';
            else if (bassAlter === '-1') bassNote += 'b';
            if (bassNote) chord += '/' + bassNote;
        }

        return chord;
    } catch (e) {
        console.error('Error parsing MusicXML for chord:', e);
    }
    return null;
}

export function updateChordInMusicXml(
    musicXml: string,
    measureNumber: number,
    chord: string | null,
    partId?: string,
    measureIndex?: number,
): string | null {
    try {
        const doc = parseMusicXml(musicXml);

        // Part-Scoping: Akkordsymbol im Ziel-Part (Default = erster Part = Melodie) setzen –
        // symmetrisch zu getChordFromMeasure.
        const part = findPart(doc, partId);
        const measure = part
            ? (measureIndex === undefined
                ? findMeasureInPart(part, measureNumber)
                : findMeasureInPartByIndex(part, measureIndex))
            : null;
        if (measure) {
            // Remove existing harmony elements
            const existingHarmonies = measure.querySelectorAll('harmony');
            existingHarmonies.forEach(h => h.remove());

            // Add new harmony if chord is provided
            if (chord) {
                const parsed = parseChordSymbol(chord);
                const harmony = doc.createElement('harmony');

                // Root element
                const root = doc.createElement('root');

                const rootStep = doc.createElement('root-step');
                rootStep.textContent = parsed.root;

                root.appendChild(rootStep);
                if (parsed.alter) {
                    const rootAlter = doc.createElement('root-alter');
                    rootAlter.textContent = parsed.alter;
                    root.appendChild(rootAlter);
                }
                harmony.appendChild(root);

                // Kind element
                const kind = doc.createElement('kind');
                kind.textContent = parsed.kind;
                harmony.appendChild(kind);

                // Bass element if present
                if (parsed.bass) {
                    const bass = doc.createElement('bass');
                    const bassStep = doc.createElement('bass-step');
                    bassStep.textContent = parsed.bass.step;
                    bass.appendChild(bassStep);
                    if (parsed.bass.alter) {
                        const bassAlter = doc.createElement('bass-alter');
                        bassAlter.textContent = parsed.bass.alter;
                        bass.appendChild(bassAlter);
                    }
                    harmony.appendChild(bass);
                }

                // Insert harmony as first child of measure
                const firstChild = measure.firstChild;
                if (firstChild) {
                    measure.insertBefore(harmony, firstChild);
                } else {
                    measure.appendChild(harmony);
                }
            }
        }

        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error updating chord in MusicXML:', e);
        return null;
    }
}

// ============ Tonart (<key>) ============

export interface KeySignatureInfo {
    measureNumber: number;
    measureIndex?: number;
    fifths: number;      // -7 bis 7 (negativ = Bes, positiv = Kreuze)
    mode: 'major' | 'minor';
}

// Key signature display names
export const KEY_SIGNATURES: { fifths: number; major: string; minor: string }[] = [
    {fifths: -7, major: 'Cb', minor: 'Ab'},
    {fifths: -6, major: 'Gb', minor: 'Eb'},
    {fifths: -5, major: 'Db', minor: 'Bb'},
    {fifths: -4, major: 'Ab', minor: 'F'},
    {fifths: -3, major: 'Eb', minor: 'C'},
    {fifths: -2, major: 'Bb', minor: 'G'},
    {fifths: -1, major: 'F', minor: 'D'},
    {fifths: 0, major: 'C', minor: 'A'},
    {fifths: 1, major: 'G', minor: 'E'},
    {fifths: 2, major: 'D', minor: 'B'},
    {fifths: 3, major: 'A', minor: 'F#'},
    {fifths: 4, major: 'E', minor: 'C#'},
    {fifths: 5, major: 'B', minor: 'G#'},
    {fifths: 6, major: 'F#', minor: 'D#'},
    {fifths: 7, major: 'C#', minor: 'A#'},
];

/** Liest die am Zieltakt **geltende** (kaskadierte) Tonart eines Parts (Default = erster Part). */
export function getKeySignatureFromMeasure(
    musicXml: string,
    measureNumber: number,
    partId?: string,
    measureIndex?: number,
): KeySignatureInfo | null {
    const doc = parseMusicXml(musicXml);

    // Part-Scoping: nur die Takte EINES Parts durchlaufen (Default = erster Part). Ein globales
    // querySelectorAll('measure') liefe in mehrteiligen Partituren nach Part 1 in Part 2 weiter
    // und läse dort eine falsche „geltende" Tonart.
    const part = findPart(doc, partId);
    const measures = part ? part.querySelectorAll('measure') : [];
    const targetMeasure = part && measureIndex !== undefined
        ? findMeasureInPartByIndex(part, measureIndex)
        : null;
    if (measureIndex !== undefined && !targetMeasure) return null;
    let currentKey: KeySignatureInfo | null = null;

    for (const measure of measures) {
        const measureNum = parseInt(measure.getAttribute('number') || '0', 10);

        // Check if this measure has a key signature
        const keyEl = measure.querySelector('attributes > key');
        if (keyEl) {
            const fifthsEl = keyEl.querySelector('fifths');
            const modeEl = keyEl.querySelector('mode');

            currentKey = {
                measureNumber: measureNum,
                fifths: fifthsEl ? parseInt(fifthsEl.textContent || '0', 10) : 0,
                mode: (modeEl?.textContent as 'major' | 'minor') || 'major',
            };
        }

        // If we've reached or passed the target measure, return what we have
        const reachedTarget = targetMeasure ? measure === targetMeasure : measureNum >= measureNumber;
        if (reachedTarget) {
            if (currentKey) {
                return {
                    ...currentKey,
                    measureNumber,
                    ...(measureIndex === undefined ? {} : {measureIndex}),
                };
            }
            break;
        }
    }

    // Return the last found key signature or default to C major
    return currentKey
        ? {...currentKey, measureNumber, ...(measureIndex === undefined ? {} : {measureIndex})}
        : {measureNumber, ...(measureIndex === undefined ? {} : {measureIndex}), fifths: 0, mode: 'major'};
}

/**
 * Setzt die Tonart. Ein Tonartwechsel gilt musikalisch score-weit → auf die `<measure number="N">`
 * JEDES Parts anwenden. (Vorher wurde nur der erste Treffer eines globalen
 * querySelectorAll('measure') geändert – in mehrteiligen Partituren bekam nur der erste Part die
 * neue Tonart und die Parts liefen mit widersprüchlichen Vorzeichen auseinander.)
 */
export function updateKeySignatureInMusicXml(
    musicXml: string,
    measureNumber: number,
    fifths: number,
    mode: 'major' | 'minor',
    measureIndex?: number,
): string | null {
    const doc = parseMusicXml(musicXml);

    const targetMeasures = getParts(doc)
        .map(part => measureIndex === undefined
            ? findMeasureInPart(part, measureNumber)
            : findMeasureInPartByIndex(part, measureIndex))
        .filter((m): m is Element => m !== null);

    if (!targetMeasures.length) {
        console.warn(`[KeySignature] Measure ${measureNumber} not found`);
        return null;
    }

    for (const targetMeasure of targetMeasures) {
        // Find or create attributes element
        let attributes = targetMeasure.querySelector('attributes');
        if (!attributes) {
            attributes = doc.createElement('attributes');
            // Insert attributes at the beginning of the measure (after any print/direction elements)
            const firstNote = targetMeasure.querySelector('note');
            if (firstNote) {
                targetMeasure.insertBefore(attributes, firstNote);
            } else {
                targetMeasure.appendChild(attributes);
            }
        }

        // Find or create key element
        let keyEl = attributes.querySelector('key');
        if (!keyEl) {
            keyEl = doc.createElement('key');
            // Insert after divisions, clef, time if they exist, or at the end
            const timeEl = attributes.querySelector('time');
            const clefEl = attributes.querySelector('clef');
            if (timeEl) {
                timeEl.after(keyEl);
            } else if (clefEl) {
                clefEl.after(keyEl);
            } else {
                attributes.appendChild(keyEl);
            }
        }

        // Update fifths
        let fifthsEl = keyEl.querySelector('fifths');
        if (!fifthsEl) {
            fifthsEl = doc.createElement('fifths');
            keyEl.appendChild(fifthsEl);
        }
        fifthsEl.textContent = fifths.toString();

        // Update mode
        let modeEl = keyEl.querySelector('mode');
        if (!modeEl) {
            modeEl = doc.createElement('mode');
            keyEl.appendChild(modeEl);
        }
        modeEl.textContent = mode;
    }

    return serializeMusicXml(doc);
}

/** Anzeigename einer Tonart (z. B. "G Dur"). */
export function getKeyDisplayName(fifths: number, mode: 'major' | 'minor'): string {
    const keySig = KEY_SIGNATURES.find(k => k.fifths === fifths);
    if (!keySig) return 'Unknown';

    const keyName = mode === 'major' ? keySig.major : keySig.minor;
    return `${keyName} ${mode === 'major' ? 'Dur' : 'Moll'}`;
}

/** Vorzeichen-Beschreibung (z. B. "2 Kreuze"). */
export function getAccidentalsDescription(fifths: number): string {
    if (fifths === 0) return 'Keine Vorzeichen';
    if (fifths > 0) return `${fifths} Kreuz${fifths > 1 ? 'e' : ''}`;
    return `${Math.abs(fifths)} B${Math.abs(fifths) > 1 ? 's' : ''}`;
}

// ============ Taktart (<time>) ============
//
// Die Taktart gilt (wie die Tonart) musikalisch **score-weit** – geschrieben wird daher in die
// `<measure number="N">` **jedes** Parts. Anders als bei der Tonart bestimmt die Taktart aber die
// zulässige Taktdauer; ein Metrum, in das der vorhandene Takt-Inhalt nicht passt, würde den Takt
// **überfüllen** und beim Export (`score.write('musicxml')` → makeNotation) eine `MeterException`
// werfen. Deshalb prüft {@link measureIsOverfullForMeter} vor dem Speichern, ob eine Stimme länger
// als das neue Metrum wäre; das ist eine **reine Notations-Korrektur** (Noten bleiben
// unverändert), **kein** Re-Barring.

export interface TimeSignatureInfo {
    measureNumber: number;
    measureIndex?: number;
    beats: number;     // Zähler (z. B. 3 in 3/4)
    beatType: number;  // Nenner (z. B. 4 in 3/4)
}

// Gebräuchliche Taktarten für die Schnellauswahl.
export const COMMON_TIME_SIGNATURES: { beats: number; beatType: number }[] = [
    {beats: 2, beatType: 4},
    {beats: 3, beatType: 4},
    {beats: 4, beatType: 4},
    {beats: 6, beatType: 8},
    {beats: 3, beatType: 8},
    {beats: 2, beatType: 2},
    {beats: 9, beatType: 8},
    {beats: 12, beatType: 8},
];

// Erlaubte Nenner (Notenwert-Basis) für den Detail-Editor.
export const BEAT_TYPES = [1, 2, 4, 8, 16];

/** Erwartete Taktdauer in `<divisions>`-Ticks: ein Viertel = `divisions` Ticks.
 *  (Auch Konsument: `structure.ts::insertMeasureInMusicXml` – Pausenfüllung neuer Takte.) */
export function expectedMeasureTicks(beats: number, beatType: number, divisions: number): number {
    if (divisions <= 0 || beatType <= 0) return 0;
    return divisions * 4 * beats / beatType;
}

/**
 * Liest die am Zieltakt **geltende** (kaskadierte) Taktart eines Parts (Default = erster Part).
 * Analog zu getKeySignatureFromMeasure – ein `<time>` wirkt bis zur nächsten Änderung nach.
 */
export function getTimeSignatureFromMeasure(
    musicXml: string, measureNumber: number, partId?: string, measureIndex?: number,
): TimeSignatureInfo | null {
    const doc = parseMusicXml(musicXml);

    // Part-Scoping: nur die Takte EINES Parts durchlaufen. Ein globales querySelectorAll('measure')
    // liefe in mehrteiligen Partituren nach Part 1 in Part 2 weiter und läse eine falsche Taktart.
    const part = findPart(doc, partId);
    const measures = part ? part.querySelectorAll('measure') : [];
    const targetMeasure = part && measureIndex !== undefined
        ? findMeasureInPartByIndex(part, measureIndex)
        : null;
    if (measureIndex !== undefined && !targetMeasure) return null;
    let currentTime: TimeSignatureInfo | null = null;

    for (const measure of measures) {
        const measureNum = parseInt(measure.getAttribute('number') || '0', 10);

        const timeEl = measure.querySelector('attributes > time');
        if (timeEl) {
            const beatsEl = timeEl.querySelector('beats');
            const beatTypeEl = timeEl.querySelector('beat-type');
            currentTime = {
                measureNumber: measureNum,
                beats: beatsEl ? parseInt(beatsEl.textContent || '4', 10) : 4,
                beatType: beatTypeEl ? parseInt(beatTypeEl.textContent || '4', 10) : 4,
            };
        }

        const reachedTarget = targetMeasure ? measure === targetMeasure : measureNum >= measureNumber;
        if (reachedTarget) {
            if (currentTime) return {
                ...currentTime,
                measureNumber,
                ...(measureIndex === undefined ? {} : {measureIndex}),
            };
            break;
        }
    }

    return currentTime
        ? {...currentTime, measureNumber, ...(measureIndex === undefined ? {} : {measureIndex})}
        : {measureNumber, ...(measureIndex === undefined ? {} : {measureIndex}), beats: 4, beatType: 4};
}

/**
 * Setzt die Taktart score-weit auf `<measure number="N">` JEDES Parts (die Taktart gilt global –
 * anders als der part-scoped Akkord). Erzeugt `<attributes>`/`<time>` bei Bedarf; `<time>` wird nach
 * `<key>`/`<divisions>` (vor `<clef>`) eingefügt (MusicXML-Reihenfolge divisions,key,time,clef).
 */
export function updateTimeSignatureInMusicXml(
    musicXml: string, measureNumber: number, beats: number, beatType: number, measureIndex?: number,
): string | null {
    const doc = parseMusicXml(musicXml);

    const targetMeasures = getParts(doc)
        .map(part => measureIndex === undefined
            ? findMeasureInPart(part, measureNumber)
            : findMeasureInPartByIndex(part, measureIndex))
        .filter((m): m is Element => m !== null);

    if (!targetMeasures.length) {
        console.warn(`[TimeSignature] Measure ${measureNumber} not found`);
        return null;
    }

    for (const targetMeasure of targetMeasures) {
        let attributes = targetMeasure.querySelector('attributes');
        if (!attributes) {
            attributes = doc.createElement('attributes');
            const firstNote = targetMeasure.querySelector('note');
            if (firstNote) {
                targetMeasure.insertBefore(attributes, firstNote);
            } else {
                targetMeasure.appendChild(attributes);
            }
        }

        let timeEl = attributes.querySelector('time');
        if (!timeEl) {
            timeEl = doc.createElement('time');
            // Reihenfolge divisions, key, time, clef: nach key bzw. divisions einhängen.
            const keyEl = attributes.querySelector('key');
            const divisionsEl = attributes.querySelector('divisions');
            const clefEl = attributes.querySelector('clef');
            if (keyEl) {
                keyEl.after(timeEl);
            } else if (divisionsEl) {
                divisionsEl.after(timeEl);
            } else if (clefEl) {
                clefEl.before(timeEl);
            } else {
                attributes.appendChild(timeEl);
            }
        }

        let beatsEl = timeEl.querySelector('beats');
        if (!beatsEl) {
            beatsEl = doc.createElement('beats');
            timeEl.appendChild(beatsEl);
        }
        beatsEl.textContent = beats.toString();

        let beatTypeEl = timeEl.querySelector('beat-type');
        if (!beatTypeEl) {
            beatTypeEl = doc.createElement('beat-type');
            timeEl.appendChild(beatTypeEl);
        }
        beatTypeEl.textContent = beatType.toString();
    }

    return serializeMusicXml(doc);
}

/**
 * Prüft, ob der Zieltakt in **irgendeinem** Part für das neue Metrum **zu klein** wäre (eine Stimme
 * enthielte mehr Ticks als das Metrum erlaubt) – genau die Bedingung, die beim Export eine
 * `MeterException` wirft. Unterfüllte Takte (Auftakt/Pickup, Content ≤ erwartet) bleiben erlaubt –
 * music21 schreibt sie problemlos. `<chord/>`-Begleitnoten teilen sich die Zeit der Hauptnote und
 * werden bei der Summe ausgeklammert.
 */
export function measureIsOverfullForMeter(
    musicXml: string, measureNumber: number, beats: number, beatType: number, measureIndex?: number,
): boolean {
    const doc = parseMusicXml(musicXml);

    for (const part of getParts(doc)) {
        const measure = measureIndex === undefined
            ? findMeasureInPart(part, measureNumber)
            : findMeasureInPartByIndex(part, measureIndex);
        if (!measure) continue;
        const divisions = getPartDivisions(part);
        if (!divisions) continue; // ohne Dauer-Basis nicht beurteilbar
        const expected = expectedMeasureTicks(beats, beatType, divisions);
        if (expected <= 0) continue;

        const voices = new Set([...measure.querySelectorAll('note')].map(voiceOf));
        for (const v of voices) {
            const sum = getVoiceNotes(measure, v)
                .filter(n => !n.querySelector('chord'))
                .reduce((acc, n) => acc + noteDurationTicks(n), 0);
            if (sum > expected) return true;
        }
    }
    return false;
}

/** Anzeigename (z. B. "3/4"). */
export function getTimeSignatureDisplayName(beats: number, beatType: number): string {
    return `${beats}/${beatType}`;
}
